// DSGVO / GDPR cookie-consent banner.
// Shown on first visit; three actions:
//   1. "Alle akzeptieren"  → analytics + marketing = true
//   2. "Nur Notwendige"    → analytics + marketing = false
//   3. "Einstellungen"     → expands modal with per-category toggles
//
// Legal basis: DSGVO §6(1)(a) — explicit opt-in for non-essential cookies
// (EuGH Planet49 ruling, C-673/17). "Necessary" cookies (session, auth,
// booking id, i18n choice) require no consent.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Megaphone, X, ChevronRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import useConsent from "../hooks/useConsent";

export default function CookieConsent() {
  const { t } = useLang();
  const c = t.cookie_consent || {};
  const { showBanner, consent, acceptAll, declineAll, save } = useConsent();
  const [mode, setMode] = useState("banner"); // "banner" | "settings"
  const [choice, setChoice] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
  });

  if (!showBanner) return null;

  const submitSelection = () => {
    save({ analytics: !!choice.analytics, marketing: !!choice.marketing });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={c.aria_label || "Cookie-Einstellungen"}
      className="fixed inset-0 z-[80] pointer-events-none"
      data-testid="cookie-consent"
    >
      {/* Dim backdrop when settings modal is open (not for the bottom banner) */}
      {mode === "settings" && (
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
          onClick={() => setMode("banner")}
        />
      )}

      <div
        className={`absolute pointer-events-auto shadow-2xl bg-white border border-neutral-200 ${
          mode === "settings"
            ? "top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2 max-w-2xl w-[min(94vw,42rem)] max-h-[90vh] overflow-y-auto"
            : "bottom-4 inset-x-4 md:bottom-6 md:inset-x-6 md:end-6 md:start-auto md:max-w-md"
        }`}
        data-testid={`cookie-consent-${mode}`}
      >
        {mode === "banner" && (
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex-shrink-0 bg-[#FF5A00]/10 text-[#FF5A00] flex items-center justify-center">
                <Cookie size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-black tracking-tight mb-1.5">{c.banner_title}</h2>
                <p className="text-xs sm:text-sm text-neutral-600 leading-relaxed">
                  {c.banner_body}{" "}
                  <Link to="/datenschutz" className="underline hover:text-[#FF5A00]">
                    {c.privacy_link}
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={acceptAll}
                className="flex-1 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wider text-xs px-4 py-3 transition-colors"
                data-testid="cookie-accept-all"
              >
                {c.accept_all}
              </button>
              <button
                type="button"
                onClick={declineAll}
                className="flex-1 bg-white hover:bg-neutral-100 text-neutral-900 border border-neutral-300 font-bold uppercase tracking-wider text-xs px-4 py-3 transition-colors"
                data-testid="cookie-reject-all"
              >
                {c.only_necessary}
              </button>
              <button
                type="button"
                onClick={() => setMode("settings")}
                className="text-neutral-500 hover:text-neutral-900 font-bold uppercase tracking-wider text-xs px-4 py-3 inline-flex items-center justify-center gap-1 transition-colors"
                data-testid="cookie-open-settings"
              >
                {c.settings} <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}

        {mode === "settings" && (
          <div className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A00] mb-1">DSGVO / GDPR</p>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">{c.settings_title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setMode("banner")}
                aria-label="Close"
                className="w-9 h-9 flex items-center justify-center hover:bg-neutral-100 transition-colors"
                data-testid="cookie-settings-close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-neutral-600 leading-relaxed mb-6">{c.settings_body}</p>

            <div className="space-y-3">
              <Category
                icon={Shield}
                title={c.cat_necessary_title}
                body={c.cat_necessary_body}
                sublist={[c.item_session, c.item_lang, c.item_booking_id]}
                required
                checked={true}
              />
              <Category
                icon={BarChart3}
                title={c.cat_analytics_title}
                body={c.cat_analytics_body}
                sublist={[c.item_teasers, c.item_chat_meta]}
                checked={choice.analytics}
                onChange={(v) => setChoice((s) => ({ ...s, analytics: v }))}
                testid="cookie-toggle-analytics"
              />
              <Category
                icon={Megaphone}
                title={c.cat_marketing_title}
                body={c.cat_marketing_body}
                sublist={[c.item_referral, c.item_email_track]}
                checked={choice.marketing}
                onChange={(v) => setChoice((s) => ({ ...s, marketing: v }))}
                testid="cookie-toggle-marketing"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row-reverse gap-2">
              <button
                type="button"
                onClick={submitSelection}
                className="flex-1 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                data-testid="cookie-save-selection"
              >
                {c.save_selection}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="bg-neutral-100 hover:bg-neutral-200 text-neutral-900 font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                data-testid="cookie-accept-all-inside"
              >
                {c.accept_all}
              </button>
              <button
                type="button"
                onClick={declineAll}
                className="bg-white hover:bg-neutral-100 text-neutral-900 border border-neutral-300 font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
                data-testid="cookie-reject-all-inside"
              >
                {c.only_necessary}
              </button>
            </div>

            <p className="mt-6 text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-4">
              {c.footer_note}{" "}
              <Link to="/datenschutz" className="underline hover:text-[#FF5A00]">
                {c.privacy_link}
              </Link>{" "}
              ·{" "}
              <Link to="/impressum" className="underline hover:text-[#FF5A00]">
                {c.imprint_link}
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Category({ icon: Icon, title, body, sublist, checked, onChange, required, testid }) {
  return (
    <div className="border border-neutral-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center ${required ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-700"}`}>
            <Icon size={14} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black tracking-tight">{title}</div>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{body}</p>
          </div>
        </div>
        {required ? (
          <span className="text-[10px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-1 whitespace-nowrap">
            {"Immer"}
          </span>
        ) : (
          <Toggle checked={checked} onChange={onChange} testid={testid} />
        )}
      </div>
      {sublist && sublist.filter(Boolean).length > 0 && (
        <ul className="mt-3 ms-11 space-y-1 text-[11px] text-neutral-500">
          {sublist.filter(Boolean).map((s) => (
            <li key={s} className="flex items-start gap-1.5">
              <span className="text-[#FF5A00] mt-0.5">•</span> {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#FF5A00]" : "bg-neutral-300"}`}
      data-testid={testid}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
